import * as React from 'react'
import { Icons, Typography } from '@lacedltd/laced-components'
import * as RadixNav from '@radix-ui/react-navigation-menu'
import { useLockBodyScroll } from 'react-use'
import { useUser, useLocale, useTranslation } from 'hooks'
import { NavItem } from '../TopNav.types'
import * as SS from '../TopNav.styles'
import * as S from './DesktopNavMenu.styles'
import type { Browse } from '../TopNav.types'

const BrowseMegaMenu = React.lazy(() => import('../BrowseMegaMenu'))

const DesktopNavMenu = ({ browse }: DesktopNavMenuProps) => {
  const { user, isLoggedIn, fetching } = useUser()
  const { t } = useTranslation()
  const { localiseUrl } = useLocale()

  const [showBackdrop, setShowBackdrop] = React.useState(false)
  const [lockScroll, setLockScroll] = React.useState(false)

  useLockBodyScroll(lockScroll)

  if (fetching) return

  const myLacedNavItems: NavItem[] = [
    {
      title: t('react_components.top_nav.my_selling'),
      href: localiseUrl('/account/selling')
    },
    {
      title: t('react_components.top_nav.my_orders'),
      href: localiseUrl('/account/orders')
    },
    {
      title: t('shared.my_account'),
      href: localiseUrl('/account')
    },
    ...(user?.isStaff
      ? [
          {
            title: t('react_components.top_nav.laced_admin'),
            href: localiseUrl('/admin')
          }
        ]
      : []),
    {
      title: t('react_components.top_nav.logout'),
      href: localiseUrl('/users/sign_out')
    }
  ]

  const sellerLink = user?.stripe_uid
    ? '/account/selling'
    : '/account/selling/verify'

  const ListItem = ({ title, href }: { title: string; href: string }) => (
    <RadixNav.Link href={href}>
      <Typography.Text variant="footnote" color="airJordan.600">
        {title}
      </Typography.Text>
    </RadixNav.Link>
  )

  const CommonNavLink = ({ href, text }: { href: string; text: string }) => {
    return (
      <RadixNav.Item>
        <S.NavLinkSimple href={localiseUrl(href)}>
          <Typography.Text variant="footnote" color="airJordan.p">
            {t(`react_components.top_nav.${text}`)}
          </Typography.Text>
        </S.NavLinkSimple>
      </RadixNav.Item>
    )
  }

  return (
    <S.DesktopNavList data-testid="desktop-nav-list">
      {showBackdrop ? <SS.NavbarBackdrop /> : null}
      <CommonNavLink href="/new-in" text="new_releases" />

      <CommonNavLink href="/best-sellers" text="best_sellers" />

      <RadixNav.Item
        onMouseEnter={() => {
          setShowBackdrop(true)
          setLockScroll(true)
        }}
        onMouseLeave={() => {
          setShowBackdrop(false)
          setLockScroll(false)
        }}
      >
        <S.BrowseNavItemTrigger
          showBackdrop={showBackdrop}
          asChild
          data-testid="mega-menu-trigger"
          onClick={() => {
            setShowBackdrop(false)
            setLockScroll(false)
          }}
        >
          <S.NavLinkComplex href={localiseUrl('/search')}>
            <Typography.Text variant="footnote" color="airJordan.p">
              {t('react_components.top_nav.browse')}
            </Typography.Text>
          </S.NavLinkComplex>
        </S.BrowseNavItemTrigger>
        {browse && showBackdrop ? (
          <S.MegaMenuContent
            onEscapeKeyDown={() => {
              setShowBackdrop(false)
              setLockScroll(false)
            }}
          >
            <React.Suspense
              fallback={
                <SS.LoadingContainer>
                  <Icons.LoadingCircle />
                </SS.LoadingContainer>
              }
            >
              <BrowseMegaMenu browse={browse} />
            </React.Suspense>
          </S.MegaMenuContent>
        ) : null}
      </RadixNav.Item>

      <CommonNavLink href={sellerLink} text="sell" />

      {isLoggedIn ? (
        <RadixNav.Item
          onMouseEnter={() => {
            setLockScroll(true)
          }}
          onMouseLeave={() => {
            setLockScroll(false)
          }}
        >
          <S.NavItemTrigger asChild>
            <S.NavLinkComplex href={localiseUrl('/account')}>
              <Typography.Text variant="footnote" color="airJordan.p">
                {t('react_components.top_nav.my_laced')}
              </Typography.Text>
            </S.NavLinkComplex>
          </S.NavItemTrigger>
          <S.MyLacedContent>
            <S.HoverDropdownList>
              {myLacedNavItems.map(({ href, title }) => {
                return <ListItem key={title} title={title} href={href} />
              })}
            </S.HoverDropdownList>
          </S.MyLacedContent>
        </RadixNav.Item>
      ) : (
        <CommonNavLink href={'/users/sign_in'} text="log_in" />
      )}
    </S.DesktopNavList>
  )
}

interface DesktopNavMenuProps {
  browse?: Browse
}

export default DesktopNavMenu
